/**
 * Filtro "Usuário" do admin — o mesmo em todas as telas da plataforma Meta.
 *
 * Admin não tem operação própria: ele analisa a de todos. Por isso o filtro
 * abre sempre em "Todos os usuários" e a escolha é UMA para a sessão inteira —
 * quem escolhe Fulano em Leads e abre Flow continua vendo Fulano, sem refazer
 * o filtro tela a tela. Some no logout (clearAuthData) e ao fechar a aba.
 *
 * Para quem não é admin o componente não renderiza e o hook devolve null:
 * a tela segue no escopo de sempre.
 */
import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { getCurrentUser } from '@/services/api/auth';
import { getUsuariosDonos } from '@/services/api/users';

export const CHAVE_FILTRO_USUARIO = 'admin:filtro-usuario';

const ouvintes = new Set<() => void>();

function ler(): number | null {
    try {
        const v = sessionStorage.getItem(CHAVE_FILTRO_USUARIO);
        return v ? Number(v) : null;
    } catch {
        return null;
    }
}

let atual: number | null = ler();

function gravar(v: number | null) {
    atual = v;
    try {
        if (v == null) sessionStorage.removeItem(CHAVE_FILTRO_USUARIO);
        else sessionStorage.setItem(CHAVE_FILTRO_USUARIO, String(v));
    } catch {
        // sem storage o filtro ainda vale enquanto a página estiver aberta
    }
    ouvintes.forEach((f) => f());
}

function assinar(f: () => void) {
    ouvintes.add(f);
    return () => ouvintes.delete(f);
}

/** Zera o filtro (logout). */
export function limparFiltroUsuario() {
    gravar(null);
}

/**
 * `usuarioId`: dono escolhido pelo admin, ou null = todos.
 * Para quem não é admin é sempre null.
 */
export function useFiltroUsuarioAdmin() {
    const isAdmin = getCurrentUser()?.role === 'admin';
    const valor = useSyncExternalStore(assinar, () => atual);
    return {
        isAdmin,
        usuarioId: isAdmin ? valor : null,
        setUsuarioId: gravar,
    };
}

export default function FiltroUsuarioAdmin({
    className = 'w-56',
    onChange,
}: {
    className?: string;
    /** Efeito extra da tela ao trocar de dono (voltar para a página 1, limpar país...). */
    onChange?: (usuarioId: number | null) => void;
}) {
    const { isAdmin, usuarioId, setUsuarioId } = useFiltroUsuarioAdmin();
    const { data: usuarios = [] } = useQuery({
        queryKey: ['usuarios-donos'],
        queryFn: getUsuariosDonos,
        enabled: isAdmin,
        staleTime: 1000 * 60 * 10,
    });

    if (!isAdmin) return null;

    return (
        <div className={className}>
            <SearchableSelect
                value={usuarioId != null ? String(usuarioId) : 'all'}
                onValueChange={(v) => {
                    const novo = v === 'all' ? null : Number(v);
                    setUsuarioId(novo);
                    onChange?.(novo);
                }}
                options={[
                    { value: 'all', label: 'Todos os usuários' },
                    ...usuarios.map((u) => ({ value: String(u.id), label: u.name })),
                ]}
                placeholder="Todos os usuários"
                searchPlaceholder="Buscar usuário..."
            />
        </div>
    );
}
